import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail, describeApiError } from "@/lib/api";
import { US_STATES } from "@/lib/usStates";
import { AgricultureTaxSection } from "@/components/AgricultureTaxSection";
import { YearEndBundleSection } from "@/components/YearEndBundleSection";
import { isAgricultureBusiness } from "@/lib/industries";
import { Calculator, FilePdf, FileCsv, CircleNotch, CalendarCheck, PiggyBank, Sparkle, Target, ShieldCheck, Receipt, PencilSimple, Trash } from "@phosphor-icons/react";
import { RowanTerm } from "@/components/RowanTerm";

const money = (n) => `$${(Number(n) || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const FILING = [
  { v: "single", label: "Single" },
  { v: "married_joint", label: "Married filing jointly" },
  { v: "head_of_household", label: "Head of household" },
];

function Row({ label, value, strong, color, term }) {
  return (
    <div className="flex justify-between items-center py-2 border-b gap-3" style={{ borderColor: "var(--bk-border)" }}>
      <span className="text-sm" style={{ color: "var(--bk-text-2)" }}>{term ? <RowanTerm term={term} size={12}>{label}</RowanTerm> : label}</span>
      <span className={strong ? "font-display text-lg font-bold text-right" : "text-sm font-semibold text-right"} style={{ color: color || "var(--bk-text)" }}>{value}</span>
    </div>
  );
}

export function Taxes({ businessId, entityState, business }) {
  const [status, setStatus] = useState("single");
  const [state, setState] = useState(entityState || "");
  const [other, setOther] = useState("");
  const [dependents, setDependents] = useState(0);
  const [priorTax, setPriorTax] = useState("");
  const [priorAgi, setPriorAgi] = useState("");
  const [savingPrior, setSavingPrior] = useState(false);
  const [est, setEst] = useState(null);
  const [loading, setLoading] = useState(false);
  const [year, setYear] = useState(new Date().getFullYear());
  const [dl, setDl] = useState("");
  const [estimateDl, setEstimateDl] = useState(false);
  const [logAmt, setLogAmt] = useState("");
  const [logging, setLogging] = useState(false);
  const [viewMode, setViewMode] = useState("annual");
  const [stateError, setStateError] = useState("");
  const stateRef = useRef(null);

  const scheduleCLines = useMemo(() => est?.schedule_c_lines || [], [est]);
  const viewedTotal = viewMode === "annual" ? est?.total_annual_tax : est?.quarterly_payment;
  const viewedSafeHarbor = viewMode === "annual" ? est?.safe_harbor_annual : est?.safe_harbor_quarterly;
  const viewedSetAside = viewMode === "annual" ? est?.monthly_set_aside : est?.quarterly_payment;

  const loadTaxData = async () => {
    try {
      const [{ data: profile }, { data: summary }] = await Promise.all([
        api.get(`/businesses/${businessId}/tax/profile`),
        api.get(`/businesses/${businessId}/tax/summary`),
      ]);
      setStatus(profile.filing_status || "single");
      setState(profile.state || entityState || "");
      setOther(String(profile.other_income || ""));
      setDependents(Number(profile.dependents || 0));
      setPriorTax(profile.prior_year_tax != null ? String(profile.prior_year_tax) : "");
      setPriorAgi(profile.prior_year_agi != null ? String(profile.prior_year_agi) : "");
      // A saved estimate only counts as "ready" once the required state is on file — otherwise wait for a valid run.
      setEst(profile.state || entityState ? summary : null);
    } catch {
      // quiet initial load
    }
  };

  useEffect(() => {
    api.get(`/businesses/${businessId}/setup`).then((r) => {
      if (r.data.plan?.state) setState((s) => s || r.data.plan.state);
    }).catch(() => {});
    loadTaxData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessId]);

  const estimate = async () => {
    if (!state) {
      setStateError("Select your state — state income tax depends on it.");
      stateRef.current?.focus();
      stateRef.current?.scrollIntoView({ block: "center", behavior: "smooth" });
      return;
    }
    setStateError("");
    setLoading(true);
    try {
      const { data } = await api.post(`/businesses/${businessId}/tax/estimate`, {
        filing_status: status,
        state,
        other_income: Number(other) || 0,
        dependents: Number(dependents) || 0,
      });
      setEst(data);
    } catch (err) {
      toast.error(describeApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const savePriorYear = async () => {
    setSavingPrior(true);
    try {
      await api.patch(`/businesses/${businessId}/tax/profile`, {
        filing_status: status, state, other_income: Number(other) || 0, dependents: Number(dependents) || 0,
        prior_year_tax: priorTax === "" ? null : Number(priorTax),
        prior_year_agi: priorAgi === "" ? null : Number(priorAgi),
      });
      const { data } = await api.get(`/businesses/${businessId}/tax/summary`);
      setEst(data);
      toast.success(priorTax === "" ? "Prior-year figures cleared" : "Prior-year figures saved");
    } catch (err) {
      toast.error(describeApiError(err));
    } finally {
      setSavingPrior(false);
    }
  };

  const [history, setHistory] = useState(null);
  const [logDate, setLogDate] = useState("");
  const [logNote, setLogNote] = useState("");
  const [editing, setEditing] = useState(null); // {id, amount, date, note}
  const [busyId, setBusyId] = useState(null);
  const applyHistory = (data) => {
    setHistory(data.entries || []);
    setEst((e) => (e ? { ...e, tax_savings: data.tax_savings } : e));
  };
  useEffect(() => {
    if (!est) return;
    api.get(`/businesses/${businessId}/tax/savings/entries`).then((r) => applyHistory(r.data)).catch(() => setHistory([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessId, !!est]);

  const logSavings = async () => {
    const amt = Number(logAmt);
    if (!amt) return toast.error("Enter an amount");
    setLogging(true);
    try {
      const { data } = await api.post(`/businesses/${businessId}/tax/savings/entries`, { amount: amt, date: logDate, note: logNote });
      applyHistory(data);
      setLogAmt(""); setLogNote("");
      toast.success(`Logged ${money(amt)} toward tax savings`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Couldn't log that.");
    } finally {
      setLogging(false);
    }
  };

  const saveEdit = async () => {
    if (!editing) return;
    setBusyId(editing.id);
    try {
      const { data } = await api.patch(`/businesses/${businessId}/tax/savings/entries/${editing.id}`, { amount: Number(editing.amount), date: editing.date, note: editing.note });
      applyHistory(data);
      setEditing(null);
      toast.success("Savings entry updated");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Couldn't update that entry.");
    } finally { setBusyId(null); }
  };

  const deleteEntry = async (entry) => {
    if (!window.confirm(`Remove the ${money(entry.amount)} entry from ${entry.date}? Your set-aside total will be reduced by that amount.`)) return;
    setBusyId(entry.id);
    try {
      const { data } = await api.delete(`/businesses/${businessId}/tax/savings/entries/${entry.id}`);
      applyHistory(data);
      toast.success("Savings entry removed");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Couldn't remove that entry.");
    } finally { setBusyId(null); }
  };

  const downloadScheduleC = async (fmt) => {
    setDl(fmt);
    try {
      const res = await api.get(`/businesses/${businessId}/tax/schedule-c.${fmt}?year=${year}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `schedule-c-${year}.${fmt}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Downloaded schedule-c-${year}.${fmt}`);
    } catch {
      toast.error("Couldn't generate that export.");
    } finally {
      setDl("");
    }
  };

  const downloadEstimatePdf = async () => {
    setEstimateDl(true);
    try {
      const res = await api.get(`/businesses/${businessId}/tax/estimate.pdf?view=${viewMode}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `tax-estimate-${viewMode}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Downloaded tax-estimate-${viewMode}.pdf`);
    } catch {
      toast.error("Couldn't generate the tax estimate PDF.");
    } finally {
      setEstimateDl(false);
    }
  };

  const years = [];
  for (let y = new Date().getFullYear(); y >= new Date().getFullYear() - 4; y--) years.push(y);
  const isAg = isAgricultureBusiness(business);

  return (
    <div className="space-y-6" data-testid="taxes-panel">
      <div className="bk-card p-6" data-testid="tax-estimator-page">
        <div className="flex items-start gap-3">
          <Calculator size={24} weight="duotone" color="var(--bk-accent)" className="shrink-0 mt-0.5" />
          <div className="flex-1">
            <h3 className="font-display text-xl font-bold" style={{ color: "var(--bk-text)" }}>Full tax estimator</h3>
            <p className="text-sm mt-1" style={{ color: "var(--bk-text-2)" }}>
              Estimates self-employment, federal and state income tax from the net profit in your books, to help you plan quarterly estimated payments and year-end preparation. These are planning estimates, not a filed return — confirm figures with your tax professional.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3 mt-4">
              <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="tax-filing-status" className="rounded-md border px-3 py-2.5 text-sm bg-white outline-none" style={{ borderColor: "var(--bk-border)", color: "var(--bk-text)" }}>
                {FILING.map((f) => <option key={f.v} value={f.v}>{f.label}</option>)}
              </select>
              <div>
                <select ref={stateRef} value={state} onChange={(e) => { setState(e.target.value); if (e.target.value) setStateError(""); }} aria-invalid={!!stateError} aria-describedby={stateError ? "tax-state-error" : undefined} data-testid="tax-state" className="w-full rounded-md border px-3 py-2.5 text-sm bg-white outline-none" style={stateError ? { borderColor: "var(--bk-energy)", boxShadow: "0 0 0 3px rgba(214, 90, 49, 0.18)", color: "var(--bk-text)" } : { borderColor: "var(--bk-border)", color: "var(--bk-text)" }}>
                  <option value="">Select state…</option>
                  {US_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
                {stateError && <p id="tax-state-error" role="alert" className="text-xs font-semibold mt-1.5" style={{ color: "var(--bk-energy)" }} data-testid="tax-state-error">{stateError}</p>}
              </div>
              <input type="number" placeholder="Other household income ($)" value={other} onChange={(e) => setOther(e.target.value)} data-testid="tax-other-income" className="rounded-md border px-3 py-2.5 text-sm outline-none" style={{ borderColor: "var(--bk-border)" }} />
              <input type="number" min="0" placeholder="Dependents" value={dependents} onChange={(e) => setDependents(e.target.value)} data-testid="tax-dependents" className="rounded-md border px-3 py-2.5 text-sm outline-none" style={{ borderColor: "var(--bk-border)" }} />
            </div>
            <div className="flex flex-wrap items-center gap-2 mt-4">
              <button onClick={estimate} disabled={loading} data-testid="estimate-tax-btn" className="bk-btn-primary inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold disabled:opacity-70">
                {loading ? <><CircleNotch size={16} className="animate-spin" /> Calculating…</> : <><Sparkle size={16} weight="fill" /> Estimate my taxes</>}
              </button>
              <button onClick={downloadEstimatePdf} disabled={!est || estimateDl} data-testid="download-estimate-pdf-btn" className="bk-btn-secondary inline-flex items-center gap-2 px-4 py-2.5 text-sm font-semibold disabled:opacity-70" style={{ color: "var(--bk-text)" }}>
                {estimateDl ? <CircleNotch size={16} className="animate-spin" /> : <FilePdf size={16} weight="bold" />} Export estimate PDF
              </button>
            </div>
          </div>
        </div>

        {!est && !loading && (
          <p className="text-sm mt-4 inline-flex items-center gap-2" style={{ color: "var(--bk-text-2)" }} data-testid="tax-result-pending">
            <Calculator size={16} weight="duotone" color="var(--bk-accent)" /> Select your state, then click <span className="font-semibold" style={{ color: "var(--bk-text)" }}>Estimate my taxes</span> to see your numbers.
          </p>
        )}

        {est && (
          <div className="mt-6 space-y-6" data-testid="tax-result">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="inline-flex rounded-full border p-1" style={{ borderColor: "var(--bk-border)", background: "#fff" }} data-testid="tax-view-toggle">
                {[
                  { key: "annual", label: "Annual" },
                  { key: "quarterly", label: "Quarterly" },
                ].map((item) => (
                  <button
                    key={item.key}
                    onClick={() => setViewMode(item.key)}
                    data-testid={`tax-view-${item.key}`}
                    className="px-4 py-1.5 rounded-full text-sm font-semibold transition-colors"
                    style={viewMode === item.key ? { background: "var(--bk-accent)", color: "#fff" } : { color: "var(--bk-text-2)" }}
                  >
                    {item.label}
                  </button>
                ))}
              </div>
              <p className="text-sm max-w-2xl" style={{ color: "var(--bk-text-2)" }} data-testid="tax-rowan-primary">{est.rowan_primary}</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
              <div className="rounded-[24px] p-5 text-white" style={{ background: "var(--bk-gradient-primary)" }} data-testid="quarterly-payment">
                <p className="text-xs uppercase tracking-wider font-bold" style={{ color: "rgba(255,255,255,0.78)" }}>{viewMode === "annual" ? "Estimated annual tax" : "Estimated quarterly payment"}</p>
                <p className="font-display text-4xl font-bold mt-1" style={{ color: "#fff" }}>{money(viewedTotal)}</p>
                <div className="flex items-center gap-2 mt-3 text-sm" style={{ color: "rgba(255,255,255,0.8)" }}>
                  <CalendarCheck size={18} weight="duotone" /> {est.next_due_quarter} due {est.next_due_date}
                </div>
              </div>

              <div className="bk-rowan-card rounded-[24px] p-5" data-testid="tax-set-aside-card">
                <div className="flex items-center gap-2">
                  <PiggyBank size={20} weight="duotone" color="var(--bk-accent)" />
                  <p className="text-sm font-semibold" style={{ color: "var(--bk-text)" }}><RowanTerm term="set_aside" size={12}>Tax set-aside</RowanTerm></p>
                </div>
                <p className="text-[11px] uppercase tracking-wider font-bold mt-3" style={{ color: "var(--bk-text-2)" }} data-testid="tax-set-aside-recommended-label">Recommended {viewMode === "annual" ? "monthly" : "quarterly"} set-aside</p>
                <p className="font-display text-xl font-bold" style={{ color: "var(--bk-text)" }} data-testid="tax-set-aside-recommended">
                  {money(viewedSetAside)} <span className="text-xs font-normal" style={{ color: "var(--bk-text-2)" }}>/ {viewMode === "annual" ? "month" : "quarter"}</span>
                </p>
                <p className="text-xs mt-1" style={{ color: "var(--bk-text-2)" }} data-testid="tax-set-aside-rate-note">
                  Rowan's suggestion, not money saved: estimated tax of {money(est.total_annual_tax)} is about {est.set_aside_pct}% of your {money(est.gross_income)} gross income, spread {viewMode === "annual" ? "over 12 months" : "across 4 quarters"}.
                </p>
                <div className="mt-3 pt-3 border-t" style={{ borderColor: "rgba(232,168,56,0.35)" }}>
                  <p className="text-[11px] uppercase tracking-wider font-bold" style={{ color: "var(--bk-text-2)" }} data-testid="tax-set-aside-saved-label">Actually set aside</p>
                  <p className="font-display text-xl font-bold" style={{ color: (est.tax_savings || 0) > 0 ? "var(--bk-success)" : "var(--bk-text)" }} data-testid="tax-set-aside-saved">{money(est.tax_savings || 0)}</p>
                  <p className="text-xs mt-1" style={{ color: "var(--bk-text-2)" }} data-testid="tax-set-aside-saved-note">
                    {(est.tax_savings || 0) > 0
                      ? `${est.total_annual_tax > 0 ? Math.min(100, Math.round(((est.tax_savings || 0) / est.total_annual_tax) * 100)) : 0}% of the ${money(est.total_annual_tax)} estimated annual tax · from your savings history below.`
                      : "Nothing logged yet — log each transfer to your tax account below and this total updates."}
                  </p>
                </div>
              </div>

              <div className="rounded-[24px] p-5 border" style={{ borderColor: "var(--bk-border)", background: "#fff" }} data-testid="tax-deadline-card">
                <div className="flex items-center gap-2">
                  <CalendarCheck size={18} weight="duotone" color="var(--bk-accent)" />
                  <p className="text-sm font-semibold" style={{ color: "var(--bk-text)" }}>Deadline countdown</p>
                </div>
                <p className="font-display text-3xl font-bold mt-2" style={{ color: "var(--bk-text)" }}>{est.days_until_due}</p>
                <p className="text-xs mt-1" style={{ color: "var(--bk-text-2)" }}>{est.rowan_deadline}</p>
              </div>

              <div className="rounded-[24px] p-5 border" style={{ borderColor: "var(--bk-border)", background: "#fff" }} data-testid="tax-safe-harbor-card">
                <div className="flex items-center gap-2">
                  <ShieldCheck size={18} weight="duotone" color="var(--bk-success)" />
                  <p className="text-sm font-semibold" style={{ color: "var(--bk-text)" }}>Estimated-tax target</p>
                </div>
                <p className="font-display text-3xl font-bold mt-2" style={{ color: "var(--bk-text)" }} data-testid="tax-current-year-target">{money(viewedSafeHarbor)}</p>
                <p className="text-xs font-semibold mt-1" style={{ color: "var(--bk-text)" }} data-testid="tax-current-year-target-label">
                  {money(viewedSafeHarbor)} — 90% current-year estimated-tax target{viewMode === "quarterly" ? " (per quarter)" : ""}
                </p>
                <p className="text-xs mt-1" style={{ color: "var(--bk-text-2)" }}>Based on projected current-year tax. This is an estimate and may change as income, expenses, credits, or payments change.</p>
              </div>
            </div>

            {(() => {
              const t = est.estimated_tax_targets || {};
              const py = t.prior_year || {};
              const viewedAt = (v) => (v == null ? null : viewMode === "quarterly" ? v / 4 : v);
              return (
                <div className="rounded-[24px] p-5 border" style={{ borderColor: "var(--bk-border)", background: "#fff" }} data-testid="tax-prior-year-card">
                  <p className="text-sm font-semibold" style={{ color: "var(--bk-text)" }}>Prior-year-based target (optional)</p>
                  <p className="text-xs mt-1" style={{ color: "var(--bk-text-2)" }}>Kept separate from the 90% current-year target above. Both are planning figures only — neither is a guaranteed payment amount and neither guarantees you avoid underpayment penalties.</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
                    <label className="text-xs" style={{ color: "var(--bk-text-2)" }}>Prior-year total tax
                      <input type="number" min="0" step="0.01" value={priorTax} onChange={(e) => setPriorTax(e.target.value)} placeholder="e.g. 4100.00" data-testid="tax-prior-year-tax-input" className="w-full mt-1 rounded-md border px-3 py-2 text-sm outline-none" style={{ borderColor: "var(--bk-border)", color: "var(--bk-text)" }} />
                      <span className="block mt-1">Enter the total tax shown on your prior-year federal return—not your refund, balance due, or withholding.</span>
                    </label>
                    <label className="text-xs" style={{ color: "var(--bk-text-2)" }}>Prior-year adjusted gross income (AGI)
                      <input type="number" min="0" step="0.01" value={priorAgi} onChange={(e) => setPriorAgi(e.target.value)} placeholder="e.g. 96000.00" data-testid="tax-prior-year-agi-input" className="w-full mt-1 rounded-md border px-3 py-2 text-sm outline-none" style={{ borderColor: "var(--bk-border)", color: "var(--bk-text)" }} />
                      <span className="block mt-1">Needed to tell whether the 100% or 110% prior-year threshold applies (110% when prior-year AGI was over {money(py.agi_limit || 150000)}).</span>
                    </label>
                  </div>
                  <div className="flex flex-wrap items-center gap-3 mt-3">
                    <button onClick={savePriorYear} disabled={savingPrior} data-testid="tax-prior-year-save" className="bk-btn-primary inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold disabled:opacity-70">
                      {savingPrior ? <CircleNotch size={14} className="animate-spin" /> : <ShieldCheck size={14} weight="bold" />} Save prior-year figures
                    </button>
                  </div>
                  <div className="mt-3 text-sm" data-testid="tax-prior-year-result" style={{ color: "var(--bk-text)" }}>
                    {py.entered_tax == null || py.entered_tax <= 0 ? (
                      <span style={{ color: "var(--bk-text-2)" }}>No prior-year total tax entered — no prior-year-based figure is shown.</span>
                    ) : py.threshold_pct ? (
                      <span><strong>{money(viewedAt(py.annual))}</strong> — {py.threshold_pct}% of prior-year total tax ({money(py.entered_tax)}){viewMode === "quarterly" ? ", per quarter" : ""}. The {py.threshold_pct}% threshold applies because prior-year AGI was {py.threshold_pct === 110 ? "over" : "at or under"} {money(py.agi_limit)}.</span>
                    ) : (
                      <span>Prior-year total tax entered: {money(py.entered_tax)}. At the 100% threshold: <strong>{money(viewedAt(py.amount_at_100))}</strong>; at the 110% threshold: <strong>{money(viewedAt(py.amount_at_110))}</strong>{viewMode === "quarterly" ? " (per quarter)" : ""}. Enter prior-year AGI above to determine which threshold applies.</span>
                    )}
                  </div>
                </div>
              );
            })()}

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="rounded-[24px] border p-5" style={{ borderColor: "var(--bk-border)", background: "#fff" }} data-testid="tax-breakdown-card">
                <h4 className="font-display text-lg font-bold mb-3" style={{ color: "var(--bk-text)" }}>Full breakdown</h4>
                <Row label="Gross income" value={money(est.gross_income)} />
                <Row label="Schedule C expenses" value={money(est.total_expense)} />
                <Row label="Net profit" term="net_profit" value={money(est.net_profit)} />
                {est.farm_tax_summary?.is_agriculture && <Row label="Schedule F net farm profit / loss" value={money(est.farm_tax_summary.schedule_f_net_profit)} />}
                {est.farm_tax_summary?.is_agriculture && <Row label="Form 4835 net farm rental income / loss" value={money(est.farm_tax_summary.form_4835_net_profit)} />}
                {est.farm_tax_summary?.is_agriculture && <Row label="Income subject to Schedule SE" value={money(est.farm_tax_summary.self_employment_income)} />}
                <Row label="Self-employment tax" term="se_tax" value={money(est.se_tax)} />
                <Row label="SE tax deduction" value={money(est.se_deduction)} />
                <Row label="Taxable income" term="taxable_income" value={money(est.taxable_income)} />
                <Row label="Dependent credit" value={money(est.dependent_credit)} />
                <Row label="Federal income tax" value={money(est.federal_income_tax)} />
                <Row label={est.no_state_income_tax ? `State tax — ${est.state} (no income tax)` : `State income tax — ${est.state} (${(est.state_rate * 100).toFixed(2)}%)`} value={money(est.state_income_tax)} />
                <Row label="Effective rate" value={`${est.effective_rate}%`} />
                <Row label={viewMode === "annual" ? "Estimated annual tax" : "Estimated quarterly payment"} value={money(viewedTotal)} strong />
              </div>

              <div className="space-y-4">
                <div className="rounded-[24px] border p-5" style={{ borderColor: "var(--bk-border)", background: "#fff" }} data-testid="tax-rowan-lines-card">
                  <div className="flex items-center gap-2 mb-3">
                    <Sparkle size={18} weight="duotone" color="var(--bk-accent)" />
                    <h4 className="font-display text-lg font-bold" style={{ color: "var(--bk-text)" }}>Rowan's notes</h4>
                  </div>
                  <ul className="space-y-2 text-sm" style={{ color: "var(--bk-text)" }} data-testid="tax-rowan-lines">
                    {(est.rowan_voice_lines || []).map((line) => <li key={line}>• {line}</li>)}
                  </ul>
                </div>

                <div className="rounded-[24px] border p-5" style={{ borderColor: "var(--bk-border)", background: "#fff" }} data-testid="schedule-c-summary-card">
                  <div className="flex items-center gap-2 mb-3">
                    <Receipt size={18} weight="duotone" color="var(--bk-success)" />
                    <h4 className="font-display text-lg font-bold" style={{ color: "var(--bk-text)" }}>Schedule C summary</h4>
                  </div>
                  {scheduleCLines.length === 0 ? (
                    <p className="text-sm" style={{ color: "var(--bk-text-2)" }}>No expense activity recorded yet. Once expenses are logged, they are mapped to Schedule C lines here.</p>
                  ) : (
                    <div className="space-y-2" data-testid="schedule-c-lines">
                      {scheduleCLines.map((row) => (
                        <div key={row.line} className="border-b py-2" style={{ borderColor: "var(--bk-border)" }} data-testid={`schedule-c-line-${row.line.split(" ")[1]}`}>
                          <div className="flex justify-between items-center gap-3">
                            <span className="text-sm font-medium" style={{ color: "var(--bk-text)" }}>{row.line}</span>
                            <span className="text-sm font-semibold text-right" style={{ color: "var(--bk-text)" }}>{money(row.amount)}</span>
                          </div>
                          {(row.categories || []).length > 0 && (
                            <div className="mt-1 space-y-0.5">
                              {row.categories.map((c) => (
                                <div key={c.category} className="flex justify-between gap-3 text-xs pl-3" style={{ color: "var(--bk-text-2)" }}>
                                  <span>{c.category}</span><span>{money(c.amount)}</span>
                                </div>
                              ))}
                            </div>
                          )}
                          {(row.items || []).length > 0 && (
                            <div className="mt-1.5 pl-3" data-testid="schedule-c-part-v">
                              <p className="text-[11px] uppercase tracking-[0.14em] font-semibold" style={{ color: "var(--bk-gold)" }}>Part V — itemized other expenses</p>
                              {row.items.map((it) => (
                                <div key={it.label} className="flex justify-between gap-3 text-xs" style={{ color: "var(--bk-text-2)" }}>
                                  <span>{it.label}</span><span>{money(it.amount)}</span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {est && (
          <div className="mt-6 rounded-[24px] p-5 border" style={{ borderColor: "var(--bk-border)", background: "rgba(255,255,255,0.95)" }} data-testid="savings-goal">
            <div className="flex items-center gap-2 mb-3">
              <Target size={20} weight="duotone" color="var(--bk-accent)" />
              <h4 className="font-display font-bold" style={{ color: "var(--bk-text)" }}>Tax savings goal</h4>
            </div>
            {(() => {
              const goal = est.total_annual_tax || 0;
              const saved = est.tax_savings || 0;
              const pct = goal > 0 ? Math.min(100, Math.round((saved / goal) * 100)) : 0;
              const done = goal > 0 && saved >= goal;
              return (
                <>
                  <div className="flex justify-between text-sm mb-1.5 gap-3">
                    <span style={{ color: "var(--bk-text-2)" }}>Saved <strong style={{ color: "var(--bk-text)" }}>{money(saved)}</strong> of {money(goal)}</span>
                    <span className="font-semibold shrink-0" style={{ color: done ? "var(--bk-success)" : "var(--bk-accent)" }} data-testid="savings-pct">{pct}%</span>
                  </div>
                  <div className="h-3 rounded-full overflow-hidden" style={{ background: "var(--bk-page)" }}>
                    <div className="h-3 rounded-full" style={{ width: `${pct}%`, background: done ? "var(--bk-success)" : "var(--bk-accent)", transition: "width 0.4s ease" }} />
                  </div>
                  <p className="text-xs mt-2" style={{ color: "var(--bk-text-2)" }}>
                    {done ? "Your set-aside covers the current estimated annual tax." : `90% current-year estimated-tax target: ${money(est.safe_harbor_annual)} · currently ${money(Math.max(0, est.safe_harbor_annual - saved))} short of that target.`}
                  </p>
                  <div className="grid sm:grid-cols-[1fr_auto_1fr_auto] gap-2 mt-3 items-center">
                    <input type="number" placeholder="Amount you set aside ($)" value={logAmt} onChange={(e) => setLogAmt(e.target.value)} data-testid="savings-log-input" className="rounded-md border px-3 py-2 text-sm outline-none" style={{ borderColor: "var(--bk-border)" }} />
                    <input type="date" value={logDate} max={new Date().toISOString().slice(0, 10)} onChange={(e) => setLogDate(e.target.value)} data-testid="savings-log-date" className="rounded-md border px-3 py-2 text-sm outline-none" style={{ borderColor: "var(--bk-border)", color: "var(--bk-text)" }} title="Defaults to today" />
                    <input placeholder="Note (optional) — e.g. Q2 transfer to tax account" value={logNote} onChange={(e) => setLogNote(e.target.value)} data-testid="savings-log-note" className="rounded-md border px-3 py-2 text-sm outline-none" style={{ borderColor: "var(--bk-border)" }} />
                    <button onClick={logSavings} disabled={logging} data-testid="log-savings-btn" className="bk-btn-primary inline-flex items-center justify-center gap-2 px-4 py-2 text-sm font-semibold disabled:opacity-70">
                      {logging ? <CircleNotch size={14} className="animate-spin" /> : <PiggyBank size={14} weight="bold" />} Log savings
                    </button>
                  </div>
                  <p className="text-[11px] mt-1.5" style={{ color: "var(--bk-text-2)" }}>To record a withdrawal from your tax savings, enter a negative amount.</p>

                  <div className="mt-4" data-testid="savings-history">
                    <p className="text-xs font-semibold uppercase tracking-[0.12em] mb-2" style={{ color: "var(--bk-text-2)" }}>Savings history</p>
                    {history === null ? (
                      <p className="text-xs" style={{ color: "var(--bk-text-2)" }}>Loading…</p>
                    ) : history.length === 0 ? (
                      <p className="text-sm rounded-lg border p-3" style={{ borderColor: "var(--bk-border)", color: "var(--bk-text-2)" }} data-testid="savings-history-empty">Nothing set aside yet. Each amount you log shows up here and can be edited or removed.</p>
                    ) : (
                      <div className="divide-y rounded-lg border" style={{ borderColor: "var(--bk-border)" }}>
                        {history.map((h) => (
                          <div key={h.id} className="p-2.5 flex flex-wrap items-center gap-2" data-testid={`savings-entry-${h.id}`}>
                            {editing?.id === h.id ? (
                              <>
                                <input type="number" value={editing.amount} onChange={(e) => setEditing({ ...editing, amount: e.target.value })} data-testid={`savings-edit-amount-${h.id}`} className="w-32 rounded-md border px-2.5 py-1.5 text-sm outline-none" style={{ borderColor: "var(--bk-border)" }} />
                                <input type="date" value={editing.date} max={new Date().toISOString().slice(0, 10)} onChange={(e) => setEditing({ ...editing, date: e.target.value })} data-testid={`savings-edit-date-${h.id}`} className="rounded-md border px-2.5 py-1.5 text-sm outline-none" style={{ borderColor: "var(--bk-border)", color: "var(--bk-text)" }} />
                                <input value={editing.note} onChange={(e) => setEditing({ ...editing, note: e.target.value })} placeholder="Note" data-testid={`savings-edit-note-${h.id}`} className="flex-1 min-w-[160px] rounded-md border px-2.5 py-1.5 text-sm outline-none" style={{ borderColor: "var(--bk-border)" }} />
                                <button onClick={saveEdit} disabled={busyId === h.id} data-testid={`savings-edit-save-${h.id}`} className="bk-btn-primary px-3 py-1.5 text-xs font-semibold disabled:opacity-60">{busyId === h.id ? "Saving…" : "Save"}</button>
                                <button onClick={() => setEditing(null)} data-testid={`savings-edit-cancel-${h.id}`} className="px-3 py-1.5 rounded-full border text-xs font-semibold" style={{ borderColor: "var(--bk-border)", color: "var(--bk-text)" }}>Cancel</button>
                              </>
                            ) : (
                              <>
                                <span className="text-sm font-semibold w-24 shrink-0" style={{ color: h.amount < 0 ? "var(--bk-energy)" : "var(--bk-text)" }} data-testid={`savings-entry-amount-${h.id}`}>{h.amount < 0 ? "−" : ""}{money(Math.abs(h.amount))}</span>
                                <span className="text-xs w-24 shrink-0" style={{ color: "var(--bk-text-2)" }} data-testid={`savings-entry-date-${h.id}`}>{h.date}</span>
                                <span className="text-xs flex-1 min-w-[120px] truncate" style={{ color: "var(--bk-text-2)" }} data-testid={`savings-entry-note-${h.id}`}>{h.note || "—"}</span>
                                <button onClick={() => setEditing({ id: h.id, amount: h.amount, date: h.date, note: h.note || "" })} data-testid={`savings-entry-edit-${h.id}`} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full border text-xs font-semibold" style={{ borderColor: "var(--bk-border)", color: "var(--bk-text)" }}><PencilSimple size={12} weight="bold" /> Edit</button>
                                <button onClick={() => deleteEntry(h)} disabled={busyId === h.id} data-testid={`savings-entry-delete-${h.id}`} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full border text-xs font-semibold disabled:opacity-60" style={{ borderColor: "var(--bk-border)", color: "var(--bk-energy)" }}><Trash size={12} weight="bold" /> Delete</button>
                              </>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </>
              );
            })()}
          </div>
        )}
      </div>

      {!isAg && <YearEndBundleSection businessId={businessId} business={business} />}

      <AgricultureTaxSection businessId={businessId} business={business} />

      <div className="bk-card p-6 bk-lift" data-testid="year-end-tax-pack">
        <div className="flex flex-col sm:flex-row sm:items-center gap-4 justify-between">
          <div>
            <h3 className="font-display text-xl font-bold" style={{ color: "var(--bk-text)" }}>Year-End Tax Pack</h3>
            <p className="text-sm mt-1" style={{ color: "var(--bk-text-2)" }}>A Schedule C-ready summary of income and expenses for your CPA or tax preparer.</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <select value={year} onChange={(e) => setYear(Number(e.target.value))} data-testid="tax-year" className="rounded-md border px-3 py-2.5 text-sm bg-white outline-none" style={{ borderColor: "var(--bk-border)", color: "var(--bk-text)" }}>
              {years.map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
            <button onClick={() => downloadScheduleC("pdf")} disabled={dl === "pdf"} data-testid="download-pdf-btn" className="bk-btn-primary inline-flex items-center gap-2 px-4 py-2.5 text-sm font-semibold disabled:opacity-70">
              {dl === "pdf" ? <CircleNotch size={16} className="animate-spin" /> : <FilePdf size={16} weight="bold" />} Schedule C PDF
            </button>
            <button onClick={() => downloadScheduleC("csv")} disabled={dl === "csv"} data-testid="download-csv-btn" className="bk-btn-secondary inline-flex items-center gap-2 px-4 py-2.5 text-sm font-semibold disabled:opacity-70" style={{ color: "var(--bk-text)" }}>
              {dl === "csv" ? <CircleNotch size={16} className="animate-spin" /> : <FileCsv size={16} weight="bold" />} Schedule C CSV
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}